package redmcrosco.alex.farmatch.ui

import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import farmatch.composeapp.generated.resources.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LoginScreen(
    onLoginSuccess: (String, String) -> Unit,
    onRegisterClick: () -> Unit,
    onSettingsClick: () -> Unit = {},
    currentLanguage: String = "it",
    onLanguageChange: (String) -> Unit = {},
    isLoading: Boolean = false,
    errorMessage: String? = null,
    rememberMe: Boolean = false,
    onRememberMeChange: (Boolean) -> Unit = {},
    initialEmail: String = "",
    onForgotPasswordClick: () -> Unit = {}
) {
    var email by remember { mutableStateOf(initialEmail) }
    var password by remember { mutableStateOf("") }
    
    // Forza aggiornamento email se cambia initialEmail (es. quando carica dallo storage)
    LaunchedEffect(initialEmail) {
        if (email.isBlank()) email = initialEmail
    }

    var emailError by remember { mutableStateOf<String?>(null) }
    val scrollState = rememberScrollState()

    val invalidEmailMsg = stringResource(Res.string.invalid_email)

    fun validateEmail(mail: String): Boolean {
        val emailRegex = "^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\\.[a-z]{2,}$".toRegex()
        return emailRegex.matches(mail)
    }

    Box(modifier = Modifier.fillMaxSize()) {
        // Icona Impostazioni (Ingranaggio) - Apre direttamente il dettaglio
        IconButton(
            onClick = onSettingsClick,
            modifier = Modifier
                .statusBarsPadding()
                .padding(16.dp)
                .align(Alignment.TopEnd)
        ) {
            Icon(
                imageVector = AppIcons.Settings,
                contentDescription = "Settings",
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(28.dp)
            )
        }

        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(24.dp)
                .verticalScroll(scrollState),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center
        ) {
            // Logo
            Image(
                painter = painterResource(Res.drawable.farmatch_logo_00),
                contentDescription = "Farmatch Logo",
                modifier = Modifier.height(80.dp).padding(bottom = 16.dp)
            )

            Text(
                text = stringResource(Res.string.welcome_back),
                style = MaterialTheme.typography.headlineMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.primary
            )
            Text(
                text = stringResource(Res.string.login_subtitle),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )

            Spacer(modifier = Modifier.height(32.dp))

            // Email
            OutlinedTextField(
                value = email,
                onValueChange = { 
                    email = it
                    emailError = if (it.isBlank() || validateEmail(it)) null else invalidEmailMsg
                },
                label = { Text(stringResource(Res.string.email_label)) },
                placeholder = { Text(stringResource(Res.string.email_placeholder)) },
                modifier = Modifier.fillMaxWidth(),
                isError = emailError != null,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                shape = RoundedCornerShape(12.dp)
            )
            if (emailError != null) {
                Text(
                    text = stringResource(Res.string.invalid_email),
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.labelSmall,
                    modifier = Modifier.align(Alignment.Start).padding(start = 8.dp)
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            // Password
            OutlinedTextField(
                value = password,
                onValueChange = { password = it },
                label = { Text(stringResource(Res.string.password_label)) },
                visualTransformation = PasswordVisualTransformation(),
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                modifier = Modifier.fillMaxWidth(),
                shape = RoundedCornerShape(12.dp)
            )

            Spacer(modifier = Modifier.height(4.dp))

            TextButton(
                onClick = onForgotPasswordClick,
                modifier = Modifier.align(Alignment.End)
            ) {
                Text("Password dimenticata?", style = MaterialTheme.typography.bodySmall)
            }

            Spacer(modifier = Modifier.height(4.dp))

            // Ricordami
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Checkbox(
                    checked = rememberMe,
                    onCheckedChange = onRememberMeChange,
                    colors = CheckboxDefaults.colors(checkedColor = MaterialTheme.colorScheme.primary)
                )
                Text(
                    text = stringResource(Res.string.remember_me_label),
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(start = 4.dp)
                )
            }

            Spacer(modifier = Modifier.height(16.dp))

            if (errorMessage != null) {
                Text(
                    text = errorMessage,
                    color = MaterialTheme.colorScheme.error,
                    style = MaterialTheme.typography.bodySmall,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
            }

            Button(
                onClick = {
                    if (validateEmail(email) && password.isNotBlank()) {
                        onLoginSuccess(email, password)
                    }
                },
                modifier = Modifier.fillMaxWidth().height(56.dp),
                shape = RoundedCornerShape(12.dp),
                enabled = !isLoading && email.isNotBlank() && password.isNotBlank() && emailError == null,
                colors = ButtonDefaults.buttonColors(
                    containerColor = MaterialTheme.colorScheme.primary,
                    contentColor = Color.White
                )
            ) {
                if (isLoading) {
                    CircularProgressIndicator(
                        modifier = Modifier.size(24.dp),
                        color = Color.White,
                        strokeWidth = 2.dp
                    )
                } else {
                    Text(stringResource(Res.string.login_button), fontWeight = FontWeight.Bold)
                }
            }

            Spacer(modifier = Modifier.height(16.dp))

            OutlinedButton(
                onClick = onRegisterClick,
                modifier = Modifier.fillMaxWidth().height(56.dp),
                shape = RoundedCornerShape(12.dp),
                border = androidx.compose.foundation.BorderStroke(2.dp, MaterialTheme.colorScheme.primary)
            ) {
                Text(
                    stringResource(Res.string.no_account_button),
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.Bold,
                    color = MaterialTheme.colorScheme.primary
                )
            }
        }
    }
}

/**
 * Reset password in due passi: 1) l'utente inserisce l'email e riceve un codice a 6 cifre via
 * email (valido 15 minuti); 2) inserisce il codice + la nuova password. Il server risponde sempre
 * con lo stesso messaggio generico al passo 1 indipendentemente dal fatto che l'email esista, per
 * non rivelare quali indirizzi sono registrati.
 */
@Composable
fun ForgotPasswordDialog(viewModel: AuthViewModel, onDismiss: () -> Unit) {
    var step by remember { mutableStateOf(1) }
    var email by remember { mutableStateOf("") }
    var code by remember { mutableStateOf("") }
    var newPassword by remember { mutableStateOf("") }
    var confirmPassword by remember { mutableStateOf("") }
    var infoMessage by remember { mutableStateOf<String?>(null) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    Dialog(onDismissRequest = onDismiss) {
        Card(shape = RoundedCornerShape(16.dp), modifier = Modifier.padding(8.dp)) {
            Column(modifier = Modifier.padding(24.dp).width(320.dp)) {
                Text(
                    text = "Password dimenticata",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold
                )
                Spacer(modifier = Modifier.height(16.dp))

                if (step == 1) {
                    Text(
                        "Inserisci l'email con cui ti sei registrato: ti mandiamo un codice per reimpostare la password.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    OutlinedTextField(
                        value = email,
                        onValueChange = { email = it },
                        label = { Text("Email") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                } else {
                    Text(
                        "Controlla la tua email: inserisci il codice ricevuto e la nuova password.",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(modifier = Modifier.height(12.dp))
                    OutlinedTextField(
                        value = code,
                        onValueChange = { code = it },
                        label = { Text("Codice a 6 cifre") },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    OutlinedTextField(
                        value = newPassword,
                        onValueChange = { newPassword = it },
                        label = { Text("Nuova password") },
                        singleLine = true,
                        visualTransformation = PasswordVisualTransformation(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                    Spacer(modifier = Modifier.height(8.dp))
                    OutlinedTextField(
                        value = confirmPassword,
                        onValueChange = { confirmPassword = it },
                        label = { Text("Conferma password") },
                        singleLine = true,
                        visualTransformation = PasswordVisualTransformation(),
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )
                }

                if (errorMessage != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(errorMessage!!, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall)
                }
                if (infoMessage != null) {
                    Spacer(modifier = Modifier.height(8.dp))
                    Text(infoMessage!!, color = MaterialTheme.colorScheme.primary, style = MaterialTheme.typography.bodySmall)
                }

                Spacer(modifier = Modifier.height(20.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("Annulla") }
                    Spacer(modifier = Modifier.width(8.dp))
                    Button(
                        enabled = !isLoading,
                        onClick = {
                            errorMessage = null
                            infoMessage = null
                            if (step == 1) {
                                if (email.isBlank()) {
                                    errorMessage = "Inserisci un'email."
                                    return@Button
                                }
                                isLoading = true
                                scope.launch {
                                    val resp = viewModel.requestPasswordReset(email)
                                    isLoading = false
                                    if (resp.success) {
                                        infoMessage = resp.message ?: "Codice inviato, controlla la tua email."
                                        step = 2
                                    } else {
                                        errorMessage = resp.message ?: "Errore durante l'invio del codice."
                                    }
                                }
                            } else {
                                if (code.isBlank() || newPassword.isBlank()) {
                                    errorMessage = "Compila codice e nuova password."
                                    return@Button
                                }
                                if (newPassword != confirmPassword) {
                                    errorMessage = "Le due password non coincidono."
                                    return@Button
                                }
                                isLoading = true
                                scope.launch {
                                    val resp = viewModel.confirmPasswordReset(email, code, newPassword)
                                    isLoading = false
                                    if (resp.success) {
                                        infoMessage = "Password aggiornata, ora puoi accedere."
                                        onDismiss()
                                    } else {
                                        errorMessage = resp.message ?: "Codice non valido o scaduto."
                                    }
                                }
                            }
                        }
                    ) {
                        if (isLoading) {
                            CircularProgressIndicator(modifier = Modifier.size(18.dp), strokeWidth = 2.dp, color = Color.White)
                        } else {
                            Text(if (step == 1) "Invia codice" else "Reimposta password")
                        }
                    }
                }
            }
        }
    }
}
